/**
 * @fileoverview network-deployment-errors.ts — module-root error
 *               surface (pure; safe on both planes).
 * @module @liquidefy/network-deployment
 * @license LQD Research LLC
 * @author Scott Martin
 * @since 1.0.0 (u0 — leverage uplift: the staged-capability error.
 *        Plan .claude/plans/2026-05-16_18-46_network-deployment-leverage-uplift.md)
 * @tier Infrastructure
 *
 * The binding-family sub-modules each carry their own 4-tier
 * hierarchy (credentials/binding/topology/bootstrap/runtime). This
 * file holds the ONE error that belongs to the composition root
 * itself: `NotWiredError` — thrown by an `io.*` capability that is
 * **declared in the front-door contract but whose uplift phase has
 * not landed yet**.
 *
 * This is the opposite of a lying stub. A staged method does not
 * return a plausible-looking fake; it throws a typed, self-
 * documenting error naming the phase that lands it. The devkit
 * smoke asserts every staged method throws this — so a staged
 * surface can never be mistaken for a working one.
 *
 * Pure (an `Error` subclass, no `node:*`, no effecting) — exported
 * via the control plane like every other family error class.
 */

/** Tier marker — mirrors the family 4-tier vocabulary. A staged
 *  capability is a *domain* condition: the call is well-formed, the
 *  capability is simply not wired in this build yet. */
export type NetworkDeploymentErrorTier = "domain";

export abstract class NetworkDeploymentError extends Error {
  abstract readonly tier: NetworkDeploymentErrorTier;
  constructor(
    message: string,
    readonly code: string,
    readonly context?: Readonly<Record<string, unknown>>,
  ) {
    super(message);
    this.name = new.target.name;
  }
}

/**
 * A front-door `io.*` capability is declared (the contract is
 * stable) but its leverage-uplift phase has not landed. Carries the
 * `method` and the `phase` (e.g. `"u5"`) that will wire it, so the
 * failure is actionable and the staged surface is self-describing.
 */
export class NotWiredError extends NetworkDeploymentError {
  readonly tier = "domain" as const;
  constructor(
    readonly method: string,
    readonly phase: string,
    detail?: string,
  ) {
    super(
      `io.${method} is a staged capability — it lands in uplift ` +
        `phase ${phase} and is not wired in this build` +
        (detail ? ` (${detail})` : "") +
        ". This is an honest staged surface, not a stub: it throws " +
        "rather than returning a fake result.",
      "NOT_WIRED",
      { method, phase, ...(detail ? { detail } : {}) },
    );
  }
}

/**
 * u4 — a gated deploy is PARKED awaiting human approval. Distinct
 * from a hard gate denial: the request was notified (carries the
 * `dedupKey`) and is resumable by re-invoking with an approver (or
 * an e7 `approve` command). Honest: the runtime does not fake a
 * suspended daemon-queue job — it refuses to proceed, notifies, and
 * is resumable. Flagged in ARCHITECTURE.md §u4.
 */
export class ApprovalPendingError extends NetworkDeploymentError {
  readonly tier = "domain" as const;
  constructor(
    readonly dedupKey: string,
    context?: Readonly<Record<string, unknown>>,
  ) {
    super(
      `deploy parked awaiting approval (notified; dedupKey ` +
        `"${dedupKey}") — resume by re-invoking with an approver`,
      "APPROVAL_PENDING",
      { dedupKey, ...(context ?? {}) },
    );
  }
}

/**
 * u2 — a deploy was refused by the gate chain (budget / change-
 * freeze / health / attestation / approval). Carries which gate
 * denied and why, so the refusal is actionable and ledgered.
 */
export class GateDeniedError extends NetworkDeploymentError {
  readonly tier = "domain" as const;
  constructor(
    readonly gate: string,
    readonly reason: string,
    context?: Readonly<Record<string, unknown>>,
  ) {
    super(
      `deploy refused by the "${gate}" gate: ${reason}`,
      "GATE_DENIED",
      { gate, reason, ...(context ?? {}) },
    );
  }
}
